import { htmlEscape } from '@edicom/common/string/htmlEscape';
import { interpolate } from '@edicom/common/templateLiteral/interpolate';

export function createTreeDataFactory(config, treeNodes, getValueFromScope, getPathFromModel, getSelectText) {
  const configIndexed = config.reduce((acc, { model: path, name }) => {
    // si no hay "name" definido, entonces usamos el "title" que es un texto simple traducido
    acc[path] = name;
    return acc;
  }, {});

  /**
   * Devuelve el objeto de datos adaptado para pintarlo en el EwcTree.
   * Lo construye recorriendo los datos desde el scope.
   *
   * @param {object} parentData el scope donde esta el registro padre, scope[parentRegisterName]
   * @param {string} parentModel el path del registro padre
   */
  return function createTreeData(parentData, parentModel) {
    const newNode = transformDataToTreeData(parentData, parentModel);
    return Promise.resolve(newNode);

    /**
     * Devuelve un objeto de los datos adaptado para el ewcTree,
     * recorre los datos de forma recursiva
     *
     * @param {object} data el registro padre dentro de un objeto, ej: { <name>: [childs] }
     */
    function transformDataToTreeData(data, parentModel = '') {
      const parentPath = getPathFromModel(parentModel);
      // recorremos todas las propiedades del registro
      return Object.entries(data).reduce((acc, [key, value]) => {
        const path = `${parentPath}.${key}`;

        // miramos si debemos mostrar/incluir este hijo en el arbol
        // lo incluimos si es un array y si esta declarado en el view, en metadata.tree
        if (Array.isArray(value) && treeNodes.includes(path)) {
          value.forEach((child, index) => {
            const model = `${parentModel}.${key}[${index}]`;
            const id = model;
            const data = getValueFromScope(model);
            const getTitle = getTitleFactory(path, model, getValueFromScope, getSelectText, configIndexed[path]);
            const node = { id, getTitle, model, path, data, children: transformDataToTreeData(child, model) };
            acc.push(node);
          });
        }
        return acc;
      }, []);
    }
  };
}

function getTitleFactory(path, model, getValueFromScope, getSelectText, configName = []) {
  let isDinamic = false;
  const fns = configName.map(def => {
    const [type, value] = Object.entries(def)[0];
    switch (type) {
      case 'icon':
        return () => `<ewc-icon name="${htmlEscape(value)}"></ewc-icon> `;
      case 'text':
        return () => htmlEscape(value);
      case 'html':
        return () => value;
      case 'template':
        isDinamic = true;
        const localscope = { [path.split('.').slice(-1)[0]]: getValueFromScope(model) };
        return () => {
          return interpolate(value, localscope);
          // @odeniel: si la expresión en el view no esta bien,
          // ej: "${mi.campo}" no esta protegido:
          // puede generar undefined facilmente
          // protegemos con este replaceAll
          // .replaceAll('undefined', '');
        };
      case 'value':
        isDinamic = true;
        const variablePath = `${model}.${value}`;
        const getText = getSelectText.bind(null, `${path}.${value}`);
        return () => {
          const evaluated = getValueFromScope(variablePath);
          return typeof evaluated === 'undefined' ? '' : htmlEscape(getText(evaluated));
        };
      default:
        throw new Error(`Tipo "${type}" desconocido definido en el view, en metadata.tree. Revisa el view. Tipos validos "icon", "text", "html", "template", "value".`);
    }
  });

  if (!isDinamic) {
    const title = getTitle();
    return () => title;
  }
  getTitle.isDinamic = true;
  return getTitle;

  function getTitle() {
    return fns.reduce((acc, cur) => acc + cur(), '');
  }
}
